import { Link, useNavigate, useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound, Lock } from 'lucide-react';
import { AuthLayout } from '@/components/AuthLayout';
import { FormError } from '@/components/form/FormError';
import { TextField } from '@/components/form/TextField';
import { Button } from '@/components/ui/button';
import { useSubmit } from '@/hooks/useSubmit';
import PasswordResetModel from '@/models/services/PasswordResetModel';
import { newPasswordSchema, PASSWORD_MIN_LENGTH, type NewPasswordForm } from '@/security/password';
import { BackToLogin } from '@/pages/ForgotPasswordPage';

/** Nouveau mot de passe à partir du lien reçu par e-mail (/reset-password?token=…) */
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { submit, submitting, error } = useSubmit();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NewPasswordForm>({ resolver: zodResolver(newPasswordSchema) });

  const onSubmit = ({ plainPassword }: NewPasswordForm) =>
    submit(async () => {
      await new PasswordResetModel().save({ token, plainPassword }, { silentError: true });
      navigate('/login', {
        replace: true,
        state: { notice: 'Votre mot de passe a été modifié, vous pouvez vous connecter.' },
      });
    });

  if (!token) {
    return (
      <AuthLayout title="Lien invalide" subtitle="Ce lien de réinitialisation est incomplet">
        <div className="space-y-4">
          <NewRequestLink />
          <BackToLogin />
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Nouveau mot de passe"
      subtitle={`Choisissez un mot de passe d'au moins ${PASSWORD_MIN_LENGTH} caractères`}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        <TextField
          id="plainPassword"
          label="Nouveau mot de passe"
          required
          type="password"
          autoComplete="new-password"
          icon={Lock}
          error={errors.plainPassword?.message}
          {...register('plainPassword')}
        />
        <TextField
          id="confirmation"
          label="Confirmation"
          required
          type="password"
          autoComplete="new-password"
          icon={Lock}
          error={errors.confirmation?.message}
          {...register('confirmation')}
        />

        <FormError error={error}>
          <NewRequestLink />
        </FormError>

        <Button type="submit" className="w-full" disabled={submitting}>
          <KeyRound className="w-4 h-4" />
          Enregistrer le mot de passe
        </Button>
        <BackToLogin />
      </form>
    </AuthLayout>
  );
}

function NewRequestLink() {
  return (
    <Link to="/forgot-password" className="block text-sm text-blue-600 hover:underline">
      Demander un nouveau lien
    </Link>
  );
}
